import {
  generateToggleFieldsFromFormFields,
  getFieldValueAndUnit,
  getFirstUnresolvedToggleFormFieldKey,
  isToggleFormFieldVisible,
  useGeckoForm,
} from 'components/form/formHelpers'
import {
  MeasurementApplication,
  PermissionsCheck,
  SetFieldAccessoryValue,
} from 'components/form/formTypes'
import React, {
  forwardRef,
  ReactNode,
  useEffect,
  useMemo,
  useState,
} from 'react'
import { FormProvider } from 'react-hook-form'
import { assetModelPrecision } from 'utils/constants'

import { ToggleFormField } from './formFields/ToggleFormField'
import { FormComponentProps, StyledForm } from './GeckoForm'
import { CoordinateInputTablePointAndClickApi } from './inputs/coordinatesInput/CoordinatesInputTable'
import { Coordinate } from './inputs/coordinatesInput/helpers/coordinateTranslations'

interface GeckoToggleFormProps extends FormComponentProps {
  inEditMode: boolean
  inResolveMode?: boolean
  measurementApplication?: MeasurementApplication
  permissionsCheck?: PermissionsCheck
  resetOnSubmit?: boolean
  pointAndClickApi: CoordinateInputTablePointAndClickApi | undefined
  highlightCoordinate: (coordinate: Coordinate | null) => void
  displayCoordinateUpdates: (newCoords: ReadonlyArray<Coordinate>) => void
  /**
   * Optional per-field accessory rendered inline beside each field input, keyed
   * by the field's `dataKey`. Return `null`/`undefined` for fields that should
   * have no accessory. Defaults to no accessories (bare fields, unchanged
   * layout).
   *
   * The second arg (`setFieldValue`) lets an accessory WRITE a value into the
   * field it sits beside (see `SetFieldAccessoryValue`); accessories that only
   * read (e.g. a citation picker) may ignore it.
   */
  renderFieldAccessory?: (
    dataKey: string,
    setFieldValue: SetFieldAccessoryValue
  ) => ReactNode
  /**
   * Optional predicate to hide a field's render by `dataKey`. Returning `true`
   * suppresses the field (same effect as the dependency-visibility gate) while
   * keeping it registered in the form, so its value round-trips untouched on
   * submit. Defaults to showing every field.
   */
  hideField?: (dataKey: string) => boolean
}

/**
 * Per-field display override, set when an accessory writes a value via
 * `SetFieldAccessoryValue`. The `NumberInput` is uncontrolled
 * (`defaultValue` only), so a programmatic `form.setValue` updates RHF state (and
 * saves) but does NOT refresh the visible box. Bumping `nonce` remounts just that
 * field so it re-seeds `defaultValue` from `displayValue`. Keyed by `formItemKey`.
 */
type FieldDisplayOverride = {
  displayValue: string | number | string[] | undefined
  nonce: number
}

export const GeckoToggleForm = forwardRef<
  HTMLFormElement,
  GeckoToggleFormProps
>(
  (
    {
      inEditMode,
      inResolveMode,
      measurementApplication,
      permissionsCheck,
      formFieldsByUniqueID,
      onSubmit,
      numberOfColumns = 2,
      propForm,
      resetOnSubmit,
      pointAndClickApi,
      highlightCoordinate,
      displayCoordinateUpdates,
      renderFieldAccessory,
      hideField,
    },
    ref
  ) => {
    const useForm = useGeckoForm(formFieldsByUniqueID)
    const form = propForm ?? useForm

    // Display overrides for accessory-written values (see FieldDisplayOverride).
    // Cleared when the field set changes identity (e.g. after a save+refresh
    // re-derives fields), mirroring useGeckoForm's own reset effect, so a stale
    // override never masks a freshly-persisted value.
    const [displayOverrides, setDisplayOverrides] = useState<
      Record<string, FieldDisplayOverride>
    >({})
    useEffect(() => {
      // Bail out (return the same object) when already empty so an unstable
      // `formFieldsByUniqueID` identity doesn't churn an extra render every time.
      setDisplayOverrides((prev) =>
        Object.keys(prev).length === 0 ? prev : {}
      )
    }, [formFieldsByUniqueID])

    const firstUnresolvedFormItemKey = useMemo(
      () =>
        getFirstUnresolvedToggleFormFieldKey(
          formFieldsByUniqueID,
          inResolveMode ?? false,
          form.watch,
          measurementApplication,
          permissionsCheck
        ),
      [
        formFieldsByUniqueID,
        inResolveMode,
        form.watch,
        measurementApplication,
        permissionsCheck,
      ]
    )

    return (
      <FormProvider {...form}>
        <StyledForm
          $columns={numberOfColumns}
          onSubmit={form.handleSubmit((data) => {
            onSubmit(data)
            if (resetOnSubmit) {
              form.reset()
            }
          })}
          ref={ref}>
          {formFieldsByUniqueID.map((formFieldsByUniqueID) =>
            generateToggleFieldsFromFormFields(
              formFieldsByUniqueID.formFields,
              formFieldsByUniqueID.uniqueID,
              measurementApplication,
              permissionsCheck
            ).map((formField) => {
              const shouldShow = isToggleFormFieldVisible(
                formField,
                formFieldsByUniqueID.formFields,
                form.watch,
                hideField
              )
              if (!shouldShow) return null
              // The accessory write-back path (override + setFieldValue) is built ONLY
              // when a `renderFieldAccessory` is provided. Forms without one can
              // never populate `displayOverrides`, so this keeps their render
              // byte-for-byte identical to before (no override lookup, no key
              // change, no per-field closure allocation).
              const override = renderFieldAccessory
                ? displayOverrides[formField.formItemKey]
                : undefined
              // Remounts a unit-bearing field when the global measurement system
              // flips, so the uncontrolled NumberInput re-seeds `defaultValue`
              // with the freshly-converted number (the unit label repaints on its
              // own, but the value would otherwise stay frozen at mount).
              // Unitless fields keep a stable key (no needless remount, so their
              // unsaved edits survive a toggle). Composes with the accessory
              // nonce path below.
              const unitSystemSuffix = formField.formFieldUnits
                ? `@${measurementApplication?.measurementSystem ?? ''}`
                : ''
              const field = (
                // Remount (nonce-bumped key) only when an accessory writes a
                // value, so the uncontrolled input re-seeds from `displayValue`;
                // typing never bumps the nonce, so it never remounts.
                <ToggleFormField
                  key={
                    override
                      ? `${formField.formItemKey}#${override.nonce}${unitSystemSuffix}`
                      : `${formField.formItemKey}${unitSystemSuffix}`
                  }
                  formItemKey={formField.formItemKey}
                  formField={formField.formField}
                  fieldValue={
                    override?.displayValue ?? formField.formFieldValue
                  }
                  fieldUnits={formField.formFieldUnits}
                  canEditField={formField.canEditField}
                  canReadField={formField.canReadField}
                  units={formField.units}
                  formControl={form.control}
                  formErrors={form.formState.errors}
                  formTrigger={form.trigger}
                  formSetValue={form.setValue}
                  inEditMode={inEditMode}
                  inResolveMode={inResolveMode}
                  focusOnResolve={
                    formField.formItemKey === firstUnresolvedFormItemKey
                  }
                  pointAndClickApi={pointAndClickApi}
                  highlightCoordinate={highlightCoordinate}
                  displayCoordinateUpdates={displayCoordinateUpdates}
                />
              )
              if (!renderFieldAccessory) return field

              const setFieldValue: SetFieldAccessoryValue = (
                metricValue: number
              ) => {
                // Runtime stores a number for measurement fields (matching
                // NumberInput.onChange); SimpleFormField's value union omits
                // number, hence the cast.
                form.setValue(
                  formField.formItemKey,
                  metricValue as unknown as string,
                  { shouldValidate: true }
                )
                const { fieldValue } = getFieldValueAndUnit(
                  metricValue,
                  formField.unit_magnitude,
                  formField.units,
                  assetModelPrecision
                )
                setDisplayOverrides((prev) => ({
                  ...prev,
                  [formField.formItemKey]: {
                    displayValue: fieldValue,
                    nonce: (prev[formField.formItemKey]?.nonce ?? 0) + 1,
                  },
                }))
              }
              const accessory = renderFieldAccessory(
                formField.formField.dataKey,
                setFieldValue
              )
              if (accessory === null || accessory === undefined) return field
              // Render the accessory as a sibling grid item after the field; it
              // owns its own layout (indent, etc.). In the single-column FFS grid
              // it naturally falls on the next row.
              return (
                <React.Fragment key={formField.formItemKey}>
                  {field}
                  {accessory}
                </React.Fragment>
              )
            })
          )}
        </StyledForm>
      </FormProvider>
    )
  }
)

GeckoToggleForm.displayName = 'GeckoToggleForm'
